// State model and the single reducer used by both the server and the in-browser (local) mode.
import {
  STAGE_KEYS, URL_FIELDS, STAGE_STATUSES, CLIENT_STATUSES, EVENT_KINDS, MONTH_COUNT, MAX_MONTH, MAX_BATCH, MAX_VIDEO_COUNT,
  BRIEF_KEYS, LOGIN_ID_PATTERN, stageTarget, videoCountOf, loginSlug,
  CONTRACT_BATCHES, CONTRACT_MONTHS, CONTENT_PER_MONTH, HANDLE_MAX, WEEKDAY_KEYS, PIC_ROLES, PIC_KEYS, STAGE_PIC, BRIEF_PIC,
} from './constants.js';
import { isISODate, isSupportedYear, isoDateOfTimestamp, monthNumberForDate, monthRange } from './dates.js';

export const STATE_VERSION = 5;
export const ACTIVITY_LIMIT = 1000;
// Sanity caps far above real use (15 people, ~100 clients), so a runaway client cannot grow the shared file without end (SEC-09).
export const COLLECTION_LIMITS = { team: 500, clients: 2000, batches: 20000, events: 20000 };
function roomFor(state, list, label) {
  if ((state[list] || []).length >= COLLECTION_LIMITS[list]) fail(`Too many ${label} (${COLLECTION_LIMITS[list]}). Delete old ones first.`);
}
const YEAR_MESSAGE = 'Enter a date between 2000 and 2100.';

export class ValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ValidationError';
  }
}

const MAX = { name: 120, contact: 120, role: 120, email: 200, phone: 200, notes: 5000, url: 2000 };
const CLIENT_FIELDS = ['name', 'contactName', 'email', 'phone', 'website', 'owner', 'startDate', 'status', 'notes', 'folderUrl',
  'instagram', 'tiktok', 'contractBatches', 'contractMonths', 'contentPerMonth', 'postingDays', 'pic'];
const BATCH_FIELDS = ['name', 'owner', 'dueDate', 'notes', 'batchNumber', 'videoCount', ...URL_FIELDS];
const EVENT_FIELDS = ['title', 'start', 'end', 'allDay', 'calendarType', 'kind', 'clientId', 'monthId', 'batchId', 'stage', 'owner', 'notes'];

// UUID v4 for new entities. crypto.randomUUID is missing in insecure browser contexts (plain http on a LAN IP),
// so fall back to getRandomValues.
export function createId() {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createEmptyState() {
  return { version: STATE_VERSION, team: [], clients: [], months: [], batches: [], events: [], activity: [] };
}

// ---------- validation helpers ----------

function fail(message) {
  throw new ValidationError(message);
}

function text(value, label, { required = false, max = MAX.name } = {}) {
  if (value == null) value = '';
  if (typeof value === 'number') value = String(value);
  if (typeof value !== 'string') fail(`${label} must be text.`);
  const v = value.trim();
  if (required && !v) fail(`${label} is required.`);
  if (v.length > max) fail(`${label} must be ${max} characters or fewer.`);
  return v;
}

function dateField(value, label, { required = false } = {}) {
  const v = typeof value === 'string' ? value.trim() : value == null ? '' : String(value);
  if (!v) {
    if (required) fail(`${label} is required.`);
    return '';
  }
  if (!isISODate(v)) fail(`${label} must be a valid date (YYYY-MM-DD).`);
  if (!isSupportedYear(v)) fail(YEAR_MESSAGE);
  return v;
}

function emailField(value) {
  const v = text(value, 'Email', { max: MAX.email });
  if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) fail('Enter a valid email address.');
  return v;
}

function refField(state, collection, id, label, { required = false } = {}) {
  const v = text(id, label, { max: 200 });
  if (!v) {
    if (required) fail(`${label} is required.`);
    return '';
  }
  if (!state[collection].some((e) => e.id === v)) fail(`${label} not found.`);
  return v;
}

// Whole number min–max; numeric strings are accepted (form inputs).
function intField(value, min, max, message) {
  const n = typeof value === 'string' && value.trim() ? Number(value.trim()) : value;
  if (!Number.isInteger(n) || n < min || n > max) fail(message);
  return n;
}
const batchNumberField = (v) => intField(v, 1, MAX_BATCH, `Batch number must be a whole number between 1 and ${MAX_BATCH}.`);
const monthNumberField = (v) => intField(v, 1, MAX_MONTH, `Month number must be a whole number between 1 and ${MAX_MONTH}.`);
const videoCountField = (v) => intField(v, 1, MAX_VIDEO_COUNT, `Videos must be a whole number between 1 and ${MAX_VIDEO_COUNT}.`);

function pick(obj, fields) {
  const out = {};
  for (const f of fields) if (obj && Object.hasOwn(obj, f)) out[f] = obj[f];
  return out;
}

function findOr404(list, id, label) {
  const found = typeof id === 'string' && id ? list.find((e) => e.id === id) : undefined;
  if (!found) fail(`${label} not found.`);
  return found;
}

const replaceById = (list, next) => list.map((e) => (e.id === next.id ? next : e));
const patchOf = (payload) => (payload.patch && typeof payload.patch === 'object' ? payload.patch : {});

// Notes saved from a page that may be stale (DATA-03): the patch carries `notesBase`, the text the edit started from.
// Same text as stored → nothing to change (returns a patch without notes); a base that no longer matches the stored
// text → refused, so the page re-reads and merges instead of overwriting someone else's words. Never stored.
function checkNotesBase(patch, stored) {
  if (!Object.hasOwn(patch, 'notesBase')) return patch;
  const { notesBase, ...rest } = patch;
  if (!Object.hasOwn(rest, 'notes')) return rest;
  const now = String(stored ?? '').trim();
  const clean = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : null);
  if (clean(rest.notes) === now) {
    delete rest.notes;
    return rest;
  }
  if (clean(notesBase) !== now) fail('Someone else changed these notes.');
  return rest;
}

// A real host: 'localhost', an IPv6 literal, or dot-separated labels that start and end with a letter/digit
// (so placeholders like 'n/a', '-', 'tbd' or '.' are refused instead of becoming https://n/a).
const LABEL = '[a-z0-9_](?:[a-z0-9_-]*[a-z0-9_])?';
const HOST_RE = new RegExp(`^(?:localhost|\\[[0-9a-f:.]+\\]|${LABEL}(?:\\.${LABEL})+)$`, 'i');

export function sanitizeUrl(value) {
  if (value == null) return '';
  if (typeof value !== 'string') fail('Enter a valid link.');
  let v = value.trim();
  if (!v) return '';
  if (v.length > MAX.url) fail(`Links must be ${MAX.url} characters or fewer.`);
  if (/[\s\u0000-\u001f\u007f]/.test(v)) fail('Links cannot contain spaces.');
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(v);
  // "example.com:8080/x" or "localhost:3000" look like a scheme but are a host with a port.
  const hostPort = scheme && /^\d{1,5}(?:[/?#]|$)/.test(v.slice(scheme[0].length))
    && (scheme[1].includes('.') || scheme[1].toLowerCase() === 'localhost');
  if (scheme && !hostPort) {
    if (!/^https?$/i.test(scheme[1])) fail('Links must start with http:// or https://.');
  } else {
    v = `https://${v.replace(/^\/+/, '')}`;
  }
  let parsed = null;
  try {
    parsed = new URL(v);
  } catch {
    fail('Enter a valid link.');
  }
  if (!/^https?:\/\//i.test(v) || !/^https?:$/.test(parsed.protocol) || !HOST_RE.test(parsed.hostname)) fail('Enter a valid link.');
  if (v.length > MAX.url) fail(`Links must be ${MAX.url} characters or fewer.`); // again, now with the added https:// (DATA-06)
  return v;
}

// ---------- batches ----------

// Derives status/currentStage/startedAt/completedAt from stageStatus. Batch status: all To Do → To Do; all Done → Done;
// otherwise the current stage (first not Done) decides: Review → Review, Blocked → Blocked, anything else → In Progress.
export function normalizeBatch(batch, ctx = {}) {
  const now = ctx.now || new Date().toISOString();
  const stageStatus = {};
  for (const key of STAGE_KEYS) {
    const s = batch.stageStatus?.[key];
    stageStatus[key] = s === 'Not Started' ? 'To Do' : STAGE_STATUSES.includes(s) ? s : 'To Do';
  }
  const every = (s) => STAGE_KEYS.every((k) => stageStatus[k] === s);
  const { progress, briefsDone, videoCount } = normalizeProgress(batch, stageStatus);
  const urls = {};
  for (const f of URL_FIELDS) urls[f] = typeof batch[f] === 'string' ? batch[f] : '';
  const currentStage = STAGE_KEYS.find((k) => stageStatus[k] !== 'Done') ?? 'result';
  let status = 'In Progress';
  if (every('To Do')) status = 'To Do';
  else if (every('Done')) status = 'Done';
  else if (stageStatus[currentStage] === 'Review') status = 'Review';
  else if (stageStatus[currentStage] === 'Blocked') status = 'Blocked';
  return {
    ...batch,
    ...urls,
    videoCount,
    progress,
    briefsDone,
    stageStatus,
    stageTimes: batch.stageTimes && typeof batch.stageTimes === 'object' ? batch.stageTimes : {},
    status,
    currentStage,
    startedAt: status === 'To Do' ? null : batch.startedAt || now,
    completedAt: status === 'Done' ? batch.completedAt || now : null,
  };
}

// Deliverable counts (DESIGN §7). progress[stage] is 0..MAX_VIDEO_COUNT (shown as min(count, target)); a Done stage is always full (a Done stage 03 has
// both briefs ticked); stage 03's count is the number of ticked briefs. Moving a stage back from Done keeps its count.
function normalizeProgress(batch, stageStatus) {
  const videoCount = videoCountOf(batch);
  const briefsDone = {};
  for (const k of BRIEF_KEYS) briefsDone[k] = stageStatus.videoShootEditingBrief === 'Done' || batch.briefsDone?.[k] === true;
  const progress = {};
  for (const key of STAGE_KEYS) {
    const target = stageTarget({ videoCount }, key);
    const n = Number(batch.progress?.[key]);
    if (key === 'videoShootEditingBrief') progress[key] = BRIEF_KEYS.filter((k) => briefsDone[k]).length;
    else if (stageStatus[key] === 'Done') progress[key] = target;
    // Stored counts keep up to MAX_VIDEO_COUNT so lowering the video count (by mistake) loses nothing; views clamp.
    else progress[key] = Number.isFinite(n) ? Math.min(MAX_VIDEO_COUNT, Math.max(0, Math.floor(n))) : 0;
  }
  return { progress, briefsDone, videoCount };
}

// ---------- team members (DESIGN §8) ----------

// A free login id derived from a name: 'ady', then 'ady2', 'ady3'… when taken (`taken` = Set of lowercase ids).
export function suggestLoginId(name, taken) {
  let base = loginSlug(name);
  if (base.length < 2) base = loginSlug(String(name ?? '').replace(/\s+/g, ''));
  if (base.length < 2) base = 'user';
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const id = `${base.slice(0, 40 - String(n).length)}${n}`;
    if (!taken.has(id)) return id;
  }
}

// Fills the v4 fields of stored members; an existing valid, unique loginId is kept.
function migrateTeam(team) {
  const taken = new Set();
  const valid = (m) => typeof m.loginId === 'string' && LOGIN_ID_PATTERN.test(m.loginId);
  for (const m of team) if (valid(m) && !taken.has(m.loginId)) taken.add(m.loginId);
  const seen = new Set();
  return team.map((m) => {
    let loginId = valid(m) && !seen.has(m.loginId) ? m.loginId : '';
    if (!loginId) {
      loginId = suggestLoginId(m.name, taken);
      taken.add(loginId);
    }
    seen.add(loginId);
    return {
      ...m,
      loginId,
      department: typeof m.department === 'string' ? m.department : '',
      isAdmin: m.isAdmin === true,
      active: m.active !== false,
    };
  });
}

function boolField(value, label) {
  if (typeof value !== 'boolean') fail(`${label} must be true or false.`);
  return value;
}

function loginIdField(value, team, selfId) {
  const v = text(value, 'Login ID', { required: true, max: 40 }).toLowerCase();
  if (!LOGIN_ID_PATTERN.test(v)) fail('Login ID must be 2–40 characters: lowercase letters, numbers, dots, dashes or underscores.');
  const other = team.find((t) => t.id !== selfId && String(t.loginId || '').toLowerCase() === v);
  if (other) fail(`Login ID "${v}" is already used by ${other.name}.`);
  return v;
}

const activeAdmins = (team) => team.filter((t) => t.isAdmin === true && t.active !== false).length;

// Once there is an active admin, no change may leave the team without one.
function keepAnAdmin(before, after) {
  if (activeAdmins(before) > 0 && activeAdmins(after) === 0) fail('The team needs at least one active admin.');
}

// ---------- migration ----------

// Upgrades a stored state to STATE_VERSION without changing its meaning. Idempotent: migrating a v3 state returns
// an equal state. v1 → v2: 'Not Started' → 'To Do', `activity: []`, `stageTimes: {}` on every batch.
// v2 → v3: batches get videoCount 15, progress (Done stages full, others 0), briefsDone (both ticked when stage 03 is
// Done), videoEditingBriefUrl ''; months get off: false. Batches stay in their months (numbers may repeat per client).
// v3 → v4: team members get loginId (slug of the first name, unique), department '', isAdmin false, active true.
// v4 → v5 (DESIGN §9): clients get instagram '', tiktok '', contractBatches 1, contractMonths 6, contentPerMonth 15,
// postingDays [], pic { script: '', videographer: '', editor: '' } (a PIC who is not on the team becomes '').
export function migrateState(state) {
  if (!state || typeof state !== 'object') throw new Error('Not a FRAZIER data file.');
  const version = state.version;
  if (![1, 2, 3, 4, STATE_VERSION].includes(version)) {
    throw new Error(version > STATE_VERSION ? 'This data was written by a newer version of FRAZIER.' : 'Not a FRAZIER data file.');
  }
  const list = (v) => (Array.isArray(v) ? v : []);
  const batches = list(state.batches).map((b) => {
    return normalizeBatch(b, { now: b.updatedAt || b.createdAt }); // also renames 'Not Started' → 'To Do'
  });
  const team = migrateTeam(list(state.team));
  const teamIds = new Set(team.map((t) => t.id));
  return {
    ...state,
    version: STATE_VERSION,
    team,
    clients: list(state.clients).map((c) => migrateClient(c, teamIds)),
    months: list(state.months).map((m) => (m.off === true || m.off === false ? m : { ...m, off: false })),
    batches,
    events: list(state.events),
    activity: list(state.activity),
  };
}

// `touched` (updates): only those fields are checked; the others keep their stored values, so an old value today's rules
// refuse (a link like "https://tbd") never locks the record (DATA-06).
function cleanBatchFields(input, state, touched = null) {
  const check = (f, clean) => (touched && !touched.has(f) ? input[f] ?? '' : clean(input[f]));
  const out = {
    name: text(input.name, 'Batch name', { required: true }),
    owner: check('owner', (v) => refField(state, 'team', v, 'Owner')),
    dueDate: check('dueDate', (v) => dateField(v, 'Due date')),
    notes: text(input.notes, 'Notes', { max: MAX.notes }),
  };
  for (const f of URL_FIELDS) out[f] = check(f, sanitizeUrl);
  return out;
}

// ---------- activity log & stage times ----------

const ENTRY_TYPES = { 'In Progress': 'started', Review: 'review', Blocked: 'blocked', Done: 'completed' };

function activityEntry(ctx, type, batch, stage = '', detail = '') {
  return { id: ctx.newId(), at: ctx.now, actorId: ctx.actorId, type, batchId: batch.id, clientId: batch.clientId, stage, detail };
}

// Appends entries (newest last), keeping the newest ACTIVITY_LIMIT.
function logActivity(state, entries) {
  if (!entries.length) return state;
  const activity = [...(state.activity || []), ...entries];
  return { ...state, activity: activity.length > ACTIVITY_LIMIT ? activity.slice(-ACTIVITY_LIMIT) : activity };
}

// A counter entry replaces the previous one when it is the newest entry and the same person changed the same counter
// less than 10 minutes ago (so a run of +/− clicks is one line in the feed).
const PROGRESS_MERGE_MS = 10 * 60000;
function logProgress(state, entry) {
  const log = state.activity || [];
  const last = log[log.length - 1];
  const same = last && last.type === 'progress' && last.batchId === entry.batchId && last.stage === entry.stage
    && last.actorId === entry.actorId && Date.parse(entry.at) - Date.parse(last.at) < PROGRESS_MERGE_MS;
  if (!same) return logActivity(state, [entry]);
  return { ...state, activity: [...log.slice(0, -1), { ...last, at: entry.at, detail: entry.detail }] };
}

// Compares a batch before/after an action: stamps stageTimes[stage] = { startedAt, reviewAt, doneAt } for every stage
// whose status changed and returns the activity entries. 'started' is logged only for a stage's first start,
// 'batchDone' when the whole batch becomes Done, 'assigned' when the owner changes (detail = new owner id or '').
// A 'started' entry remembers who the stage's PIC was at that moment, so a PIC named later is not told about work
// that was never theirs (ROLES-06): 'pic:<id>'; stage 03 'pic:<videographer brief PIC>,<editing brief PIC>'.
function picDetail(client, stage) {
  const pic = (role) => client?.pic?.[role] || '';
  return stage === 'videoShootEditingBrief' ? `pic:${pic(BRIEF_PIC.videographer)},${pic(BRIEF_PIC.editing)}` : `pic:${pic(STAGE_PIC[stage])}`;
}

function trackChanges(prev, next, ctx, client) {
  const stageTimes = { ...next.stageTimes };
  const entries = [];
  for (const key of STAGE_KEYS) {
    const from = prev.stageStatus?.[key];
    const to = next.stageStatus[key];
    if (from === to) continue;
    if (to === 'To Do') {
      delete stageTimes[key];
      continue;
    }
    const t = { startedAt: null, reviewAt: null, doneAt: null, ...stageTimes[key] };
    const firstStart = !t.startedAt;
    t.startedAt = t.startedAt || ctx.now;
    if (to === 'Review') t.reviewAt = ctx.now;
    t.doneAt = to === 'Done' ? ctx.now : null;
    stageTimes[key] = t;
    if (to !== 'In Progress' || firstStart) entries.push(activityEntry(ctx, ENTRY_TYPES[to], next, key, to === 'In Progress' ? picDetail(client, key) : ''));
  }
  if (prev.status !== 'Done' && next.status === 'Done') entries.push(activityEntry(ctx, 'batchDone', next, 'result'));
  if ((prev.owner || '') !== (next.owner || '')) entries.push(activityEntry(ctx, 'assigned', next, '', next.owner || ''));
  return { batch: { ...next, stageTimes }, entries };
}

function withBatch(state, id, fn, ctx) {
  const batch = findOr404(state.batches, id, 'Batch');
  const next = fn(batch);
  if (next === batch) return { state, result: { batchId: id } };
  // Any change makes the batch the user's own: Remove sample data keeps it (DATA-05).
  const normalized = normalizeBatch({ ...userOwned(next), updatedAt: ctx.now }, ctx);
  const client = state.clients.find((c) => c.id === batch.clientId);
  const { batch: tracked, entries } = trackChanges(batch, normalized, ctx, client);
  return { state: logActivity({ ...state, batches: replaceById(state.batches, tracked) }, entries), result: { batchId: id } };
}

// A record the user changed is theirs: the sample flag goes (see sample.clear).
function userOwned(record) {
  if (!record.sample) return record;
  const { sample, ...rest } = record;
  return rest;
}
const ownedIf = (patch, record) => (Object.keys(patch).length ? userOwned(record) : record); // an empty patch changes nothing

// ---------- clients ----------

// '@northwind.co' → 'northwind.co'. A pasted profile link (instagram.com/<h>, tiktok.com/@<h>) gives its handle.
const HANDLE_RE = new RegExp(`^[A-Za-z0-9._]{1,${HANDLE_MAX}}$`);
function handleField(value, label) {
  let v = text(value, label, { max: MAX.url });
  const link = /^(?:https?:\/\/)?(?:[a-z]+\.)?(?:instagram|tiktok)\.com\/@?([^/?#]+)/i.exec(v);
  if (link) v = link[1];
  v = v.replace(/^@+/, '');
  if (v && !HANDLE_RE.test(v)) fail(`${label} can only use letters, numbers, dots and underscores (${HANDLE_MAX} characters max).`);
  return v;
}

// A whole number within a contract spec ({ min, max, default }); empty → the default.
function contractField(value, spec, label) {
  if (value == null || value === '') return spec.default;
  return intField(value, spec.min, spec.max, `${label} must be a whole number between ${spec.min} and ${spec.max}.`);
}

// ['fri', 'Mon', 'monday'] → ['mon', 'fri'] (week order, no repeats).
function postingDaysField(value) {
  if (value == null || value === '') return [];
  if (!Array.isArray(value)) fail('Posting days must be a list of weekdays.');
  const keys = new Set(value.map((d) => String(d ?? '').trim().toLowerCase().slice(0, 3)));
  for (const k of keys) if (!WEEKDAY_KEYS.includes(k)) fail('Unknown posting day.');
  return WEEKDAY_KEYS.filter((k) => keys.has(k));
}

// { script, videographer, editor }: team ids or '' (missing roles → '').
function picField(value, state) {
  if (value == null) value = {};
  if (typeof value !== 'object' || Array.isArray(value)) fail('Choose team members for the PIC fields.');
  return Object.fromEntries(PIC_ROLES.map((r) => [r.key, refField(state, 'team', value[r.key], r.label)]));
}

// `touched` (updates): as in cleanBatchFields, untouched links, email and dates keep their stored values (DATA-06).
function cleanClientFields(input, state, { requireOwner, touched = null }) {
  const status = input.status == null || input.status === '' ? 'Active' : input.status;
  if (!CLIENT_STATUSES.includes(status)) fail('Unknown client status.');
  const check = (f, clean) => (touched && !touched.has(f) ? input[f] ?? '' : clean(input[f]));
  return {
    name: text(input.name, 'Client name', { required: true }),
    contactName: text(input.contactName, 'Contact name', { max: MAX.contact }),
    email: check('email', emailField),
    phone: text(input.phone, 'Phone', { max: MAX.phone }),
    website: check('website', sanitizeUrl),
    owner: refField(state, 'team', input.owner, 'Account owner', { required: requireOwner }),
    startDate: check('startDate', (v) => dateField(v, 'Start date', { required: true })),
    status,
    notes: text(input.notes, 'Notes', { max: MAX.notes }),
    folderUrl: check('folderUrl', sanitizeUrl),
    instagram: handleField(input.instagram, 'Instagram'),
    tiktok: handleField(input.tiktok, 'TikTok'),
    contractBatches: contractField(input.contractBatches, CONTRACT_BATCHES, 'Batch count'),
    contractMonths: contractField(input.contractMonths, CONTRACT_MONTHS, 'Month count'),
    contentPerMonth: contractField(input.contentPerMonth, CONTENT_PER_MONTH, 'Content per month'),
    postingDays: postingDaysField(input.postingDays),
    pic: picField(input.pic, state),
  };
}

// v4 → v5 client fields (DESIGN §9): missing or invalid values get their defaults; a PIC who is not on the team is ''.
function migrateClient(c, teamIds) {
  const count = (v, spec) => (Number.isInteger(v) && v >= spec.min && v <= spec.max ? v : spec.default);
  const str = (v) => (typeof v === 'string' ? v : '');
  const pic = c.pic && typeof c.pic === 'object' ? c.pic : {};
  return {
    ...c,
    contactName: str(c.contactName),
    instagram: str(c.instagram),
    tiktok: str(c.tiktok),
    contractBatches: count(c.contractBatches, CONTRACT_BATCHES),
    contractMonths: count(c.contractMonths, CONTRACT_MONTHS),
    contentPerMonth: count(c.contentPerMonth, CONTENT_PER_MONTH),
    postingDays: Array.isArray(c.postingDays) ? WEEKDAY_KEYS.filter((k) => c.postingDays.includes(k)) : [],
    pic: Object.fromEntries(PIC_KEYS.map((k) => [k, teamIds.has(pic[k]) ? pic[k] : ''])),
  };
}

function newMonth(client, monthNumber, ctx) {
  const month = { id: ctx.newId(), clientId: client.id, monthNumber, ...monthRange(client.startDate, monthNumber), notes: '', off: false };
  return client.sample ? { ...month, sample: true } : month; // structure of a sample client goes with it
}

// A new client starts with MONTH_COUNT months; later months are created on demand (up to MAX_MONTH).
function createMonths(client, ctx) {
  return Array.from({ length: MONTH_COUNT }, (_, i) => newMonth(client, i + 1, ctx));
}

// Makes sure the client has month records 1..n (creating the missing ones). → { state, month /* month n */ }
function ensureMonths(state, client, n, ctx) {
  const have = new Set(state.months.filter((m) => m.clientId === client.id).map((m) => m.monthNumber));
  const added = [];
  for (let k = 1; k <= n; k++) if (!have.has(k)) added.push(newMonth(client, k, ctx));
  const next = added.length ? { ...state, months: [...state.months, ...added] } : state;
  return { state: next, month: next.months.find((m) => m.clientId === client.id && m.monthNumber === n) };
}

// A month by id, or by { clientId, monthNumber } (created with the months before it when missing).
function resolveMonth(state, p, ctx) {
  if (p.id) return { state, month: findOr404(state.months, p.id, 'Month') };
  const client = findOr404(state.clients, p.clientId, 'Client');
  return ensureMonths(state, client, monthNumberField(p.monthNumber), ctx);
}

// ---------- events ----------

const DATETIME = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

function whenField(value, label, { required, allDay }) {
  const v = typeof value === 'string' ? value.trim() : '';
  if (!v) {
    if (required) fail(`${label} is required.`);
    return '';
  }
  const m = DATETIME.exec(v);
  const date = m ? m[1] : v;
  if (!isISODate(date)) fail(`${label} must be a valid date.`);
  if (!isSupportedYear(date)) fail(YEAR_MESSAGE);
  if (allDay) return date;
  if (!m) fail(`${label} time is required.`);
  if (Number(m[2]) > 23 || Number(m[3]) > 59) fail(`${label} must be a valid time.`);
  return `${date}T${m[2]}:${m[3]}`;
}

// checkSpan: timed events must end on the day they start (the calendar grid and forms are single-day). Only checked
// when the times are being set, so an older overnight event stays editable (title, notes…).
function cleanEventFields(input, state, { checkSpan = true } = {}) {
  const calendarType = input.calendarType;
  if (typeof calendarType !== 'string' || !Object.hasOwn(EVENT_KINDS, calendarType)) fail('Choose a calendar.');
  const kinds = EVENT_KINDS[calendarType];
  // No kind given: a Main Calendar event is a general event ('' — never silently a "Script Deadline"); an Internal
  // Note gets the first internal kind.
  const kind = text(input.kind, 'Event type') || (calendarType === 'main' ? '' : kinds[0]);
  if (kind && !kinds.includes(kind)) fail('Unknown event type.');

  const rawStart = typeof input.start === 'string' ? input.start.trim() : '';
  const allDay = input.allDay === undefined ? !rawStart.includes('T') : Boolean(input.allDay);
  const start = whenField(input.start, 'Start', { required: true, allDay });
  const end = whenField(input.end, 'End', { required: false, allDay });
  if (end && end < start) fail('End must be after the start.');
  if (checkSpan && !allDay && end && end.slice(0, 10) !== start.slice(0, 10)) fail('A timed event must end on the day it starts.');

  // A linked batch decides the client and month; otherwise a month implies its client.
  const batchId = refField(state, 'batches', input.batchId, 'Batch');
  let clientId = '';
  let monthId = '';
  if (batchId) {
    const batch = state.batches.find((b) => b.id === batchId);
    clientId = batch.clientId;
    monthId = batch.monthId;
  } else {
    clientId = refField(state, 'clients', input.clientId, 'Client');
    monthId = refField(state, 'months', input.monthId, 'Month');
    if (monthId) {
      const month = state.months.find((m) => m.id === monthId);
      if (clientId && month.clientId !== clientId) fail('Month does not belong to this client.');
      clientId = month.clientId;
    }
  }

  const stage = text(input.stage, 'Production stage');
  if (stage && !STAGE_KEYS.includes(stage)) fail('Unknown production stage.');

  return {
    title: text(input.title, 'Title', { required: true }),
    start,
    end,
    allDay,
    calendarType,
    kind,
    clientId,
    monthId,
    batchId,
    stage,
    owner: refField(state, 'team', input.owner, 'Owner'),
    notes: text(input.notes, 'Notes', { max: MAX.notes }),
  };
}

// ---------- action handlers ----------

const handlers = {
  // status defaults to Active. { startMonthOne: true } (DESIGN §9 "Start Month 1 now") also creates Batch 01 in
  // Month 01: name "Batch 01", owner = the Script PIC (else the account owner), videoCount = contentPerMonth,
  // due the last day of Month 01 (1 batch = 1 month of production, DESIGN §7) unless that day has already passed (a
  // client entered with an old start date gets no overdue batch). result { clientId, batchId? }
  'client.create'(state, p, ctx) {
    roomFor(state, 'clients', 'clients');
    const fields = cleanClientFields(pick(p, CLIENT_FIELDS), state, { requireOwner: true });
    const client = { id: ctx.newId(), ...fields, createdAt: ctx.now, updatedAt: ctx.now };
    const months = createMonths(client, ctx);
    const next = { ...state, clients: [...state.clients, client], months: [...state.months, ...months] };
    if (p.startMonthOne !== true && p.startMonthOne !== 'true') return { state: next, result: { clientId: client.id } };
    const monthOneEnd = monthRange(client.startDate, 1).endDate;
    const first = handlers['batch.create'](next, {
      clientId: client.id, name: 'Batch 01', monthNumber: 1, batchNumber: 1, owner: client.pic.script || client.owner, videoCount: client.contentPerMonth,
      dueDate: ctx.today && monthOneEnd < ctx.today ? '' : monthOneEnd,
    }, ctx);
    return { state: first.state, result: { clientId: client.id, batchId: first.result.batchId } };
  },

  // Only the fields sent change; `pic` may be partial ({ editor: 'id' } keeps the other two).
  'client.update'(state, p, ctx) {
    const client = findOr404(state.clients, p.id, 'Client');
    const patch = pick(patchOf(p), CLIENT_FIELDS);
    if (patch.pic && typeof patch.pic === 'object' && !Array.isArray(patch.pic)) patch.pic = { ...client.pic, ...patch.pic };
    const fields = cleanClientFields({ ...client, ...patch }, state, { requireOwner: Object.hasOwn(patch, 'owner'), touched: new Set(Object.keys(patch)) });
    const next = { ...ownedIf(patch, client), ...fields, updatedAt: ctx.now };
    if (next.startDate === client.startDate) {
      return { state: { ...state, clients: replaceById(state.clients, next) }, result: { clientId: client.id, moved: 0 } };
    }
    // Months are re-dated; batches stay in the month they were put in (Month no. is chosen by people, DESIGN §7).
    const months = state.months.map((m) => (m.clientId === client.id ? { ...m, ...monthRange(next.startDate, m.monthNumber) } : m));
    return {
      state: { ...state, clients: replaceById(state.clients, next), months },
      result: { clientId: client.id, moved: 0 },
    };
  },

  'client.delete'(state, p) {
    const client = findOr404(state.clients, p.id, 'Client');
    const gone = new Set(state.batches.filter((b) => b.clientId === client.id).map((b) => b.id));
    return {
      state: {
        ...state,
        clients: state.clients.filter((c) => c.id !== client.id),
        months: state.months.filter((m) => m.clientId !== client.id),
        batches: state.batches.filter((b) => b.clientId !== client.id),
        events: state.events.filter((e) => e.clientId !== client.id && !gone.has(e.batchId)),
        activity: (state.activity || []).filter((a) => !gone.has(a.batchId) && a.clientId !== client.id),
      },
      result: { clientId: client.id },
    };
  },

  // { id } or { clientId, monthNumber } (a month past the existing ones is created first). A month the user edits
  // (notes, Off) stops being sample data, so Remove sample data keeps it as it is.
  'month.update'(state, p, ctx) {
    const { state: s, month } = resolveMonth(state, p, ctx);
    const patch = checkNotesBase(patchOf(p), month.notes);
    const notes = Object.hasOwn(patch, 'notes') ? text(patch.notes, 'Notes', { max: MAX.notes }) : month.notes;
    if (notes === month.notes) return { state: s, result: { monthId: month.id, monthNumber: month.monthNumber } };
    const next = { ...userOwned(month), notes };
    return { state: { ...s, months: replaceById(s.months, next) }, result: { monthId: month.id, monthNumber: month.monthNumber } };
  },

  // Marks a month Off (sickness, client pause…) or back on: { id | clientId + monthNumber, off }. Logged as
  // 'monthOff' / 'monthOn' (detail = month number). Off months never count as late.
  'month.setOff'(state, p, ctx) {
    const { state: s, month } = resolveMonth(state, p, ctx);
    const off = p.off === true || p.off === 'true';
    const result = { monthId: month.id, monthNumber: month.monthNumber, off };
    if (Boolean(month.off) === off) return { state: s, result };
    const next = { ...userOwned(month), off };
    const entry = {
      id: ctx.newId(), at: ctx.now, actorId: ctx.actorId, type: off ? 'monthOff' : 'monthOn', batchId: '', clientId: month.clientId,
      monthId: month.id, stage: '', detail: String(month.monthNumber),
    };
    return { state: logActivity({ ...s, months: replaceById(s.months, next) }, [entry]), result };
  },

  // Month: monthId wins, then monthNumber (1–99; missing months are created up to it), else the client's current month
  // (by ctx.today). batchNumber (1–99) defaults to the client's highest + 1; a repeated number is allowed (the UI
  // warns) and reported as result.duplicateNumber. videoCount 1–99, default 15.
  'batch.create'(state, p, ctx) {
    roomFor(state, 'batches', 'batches');
    const client = findOr404(state.clients, p.clientId, 'Client');
    const fields = cleanBatchFields(p, state);
    let s = state;
    let month;
    if (p.monthId) {
      month = findOr404(state.months, p.monthId, 'Month');
      if (month.clientId !== client.id) fail('Month does not belong to this client.');
    } else {
      const n = p.monthNumber == null || p.monthNumber === '' ? monthNumberForDate(client.startDate, ctx.today, MAX_MONTH) : monthNumberField(p.monthNumber);
      ({ state: s, month } = ensureMonths(state, client, n, ctx));
    }
    const siblings = s.batches.filter((b) => b.clientId === client.id);
    const batchNumber = p.batchNumber == null || p.batchNumber === ''
      ? Math.min(MAX_BATCH, siblings.reduce((max, b) => Math.max(max, b.batchNumber), 0) + 1)
      : batchNumberField(p.batchNumber);
    // Default: the client's content per month (DESIGN §9), 15 for older clients.
    const videoCount = p.videoCount == null || p.videoCount === '' ? videoCountOf({ videoCount: client.contentPerMonth }) : videoCountField(p.videoCount);
    const batch = normalizeBatch({
      id: ctx.newId(),
      clientId: client.id,
      monthId: month.id,
      batchNumber,
      ...fields,
      videoCount,
      stageStatus: Object.fromEntries(STAGE_KEYS.map((k) => [k, 'To Do'])),
      stageTimes: {},
      startedAt: null,
      completedAt: null,
      createdAt: ctx.now,
      updatedAt: ctx.now,
    }, ctx);
    const next = logActivity({ ...s, batches: [...s.batches, batch] }, [activityEntry(ctx, 'created', batch, '', batch.owner || '')]);
    const duplicateNumber = siblings.some((b) => b.batchNumber === batchNumber);
    return { state: next, result: { batchId: batch.id, monthId: month.id, monthNumber: month.monthNumber, batchNumber, duplicateNumber } };
  },

  // patch: name, owner, dueDate, notes, links, batchNumber (1–99), videoCount (1–99; stored counts are kept, views
  // clamp them to the new target) and monthNumber (1–99: moves the batch and its linked events; missing months are created).
  'batch.update'(state, p, ctx) {
    const raw = checkNotesBase(patchOf(p), findOr404(state.batches, p.id, 'Batch').notes);
    const patch = pick(raw, BATCH_FIELDS);
    let s = state;
    let month = null;
    if (Object.hasOwn(raw, 'monthNumber')) {
      const current = findOr404(state.batches, p.id, 'Batch');
      const client = findOr404(state.clients, current.clientId, 'Client');
      ({ state: s, month } = ensureMonths(state, client, monthNumberField(raw.monthNumber), ctx));
    }
    const out = withBatch(s, p.id, (batch) => {
      const merged = { ...batch, ...patch };
      const batchNumber = Object.hasOwn(patch, 'batchNumber') ? batchNumberField(patch.batchNumber) : batch.batchNumber;
      const videoCount = Object.hasOwn(patch, 'videoCount') ? videoCountField(patch.videoCount) : batch.videoCount;
      return { ...batch, ...cleanBatchFields(merged, s, new Set(Object.keys(patch))), batchNumber, videoCount, monthId: month ? month.id : batch.monthId };
    }, ctx);
    if (!month) return out;
    const events = out.state.events.map((e) => (e.batchId === p.id && e.monthId !== month.id ? { ...e, monthId: month.id, updatedAt: ctx.now } : e));
    return { state: { ...out.state, events }, result: { ...out.result, monthId: month.id, monthNumber: month.monthNumber } };
  },

  // Deliverable counter of stage 01/02/04/05: { id, stage, count } (clamped to 0..target). Stage 03 uses batch.setBrief.
  // A Done stage stays full (reopen it first). Logged as 'progress' (detail 'n/target'); quick repeated changes by the
  // same person to the same counter update that one entry instead of adding more.
  // Two people on one counter (DATA-01): { delta: +1 | -1 } adds to the stored count (the −/+ buttons), and a typed
  // { count, from } is refused when the stored count is no longer `from`, the number that person saw.
  'batch.setProgress'(state, p, ctx) {
    if (!STAGE_KEYS.includes(p.stage)) fail('Unknown production stage.');
    if (p.stage === 'videoShootEditingBrief') fail('Tick the briefs to update this stage.');
    const num = (v) => (typeof v === 'string' && v.trim() ? Number(v.trim()) : v);
    const relative = p.delta != null && p.delta !== '';
    const n = num(relative ? p.delta : p.count);
    if (typeof n !== 'number' || !Number.isFinite(n)) fail('Enter a number.');
    const current = findOr404(state.batches, p.id, 'Batch');
    const target = stageTarget(current, p.stage);
    const stored = Number(current.progress?.[p.stage]) || 0;
    // (a page shows a count above a lowered target as the target, so either number counts as what was seen)
    if (!relative && p.from != null && p.from !== '' && ![stored, Math.min(target, stored)].includes(num(p.from))) fail('This counter was just changed by someone else. Check the new number.');
    const count = Math.min(target, Math.max(0, Math.round(relative ? stored + n : n)));
    if (current.progress?.[p.stage] === count) return { state, result: { batchId: current.id, count, target } };
    if (current.stageStatus?.[p.stage] === 'Done') fail('This stage is done. Change its status to edit the count.');
    // Counting on a stage that has not started starts it (visible work is never reported as "To Do").
    const out = withBatch(state, p.id, (batch) => ({
      ...batch,
      progress: { ...batch.progress, [p.stage]: count },
      stageStatus: count > 0 && batch.stageStatus?.[p.stage] === 'To Do' ? { ...batch.stageStatus, [p.stage]: 'In Progress' } : batch.stageStatus,
    }), ctx);
    const batch = out.state.batches.find((b) => b.id === p.id);
    const next = logProgress(out.state, activityEntry(ctx, 'progress', batch, p.stage, `${count}/${target}`));
    return { state: next, result: { batchId: batch.id, count, target } };
  },

  // Stage 03's two briefs: { id, which: 'videographer' | 'editing', done }. Logged as 'brief' (detail 'which:done|open').
  'batch.setBrief'(state, p, ctx) {
    if (!BRIEF_KEYS.includes(p.which)) fail('Unknown brief.');
    const done = p.done === true || p.done === 'true';
    const current = findOr404(state.batches, p.id, 'Batch');
    if (Boolean(current.briefsDone?.[p.which]) === done) return { state, result: { batchId: current.id } };
    if (!done && current.stageStatus?.videoShootEditingBrief === 'Done') fail('This stage is done. Change its status to edit the briefs.');
    const out = withBatch(state, p.id, (batch) => ({
      ...batch,
      briefsDone: { ...batch.briefsDone, [p.which]: done },
      stageStatus: done && batch.stageStatus?.videoShootEditingBrief === 'To Do' ? { ...batch.stageStatus, videoShootEditingBrief: 'In Progress' } : batch.stageStatus,
    }), ctx);
    const batch = out.state.batches.find((b) => b.id === p.id);
    const next = logActivity(out.state, [activityEntry(ctx, 'brief', batch, 'videoShootEditingBrief', `${p.which}:${done ? 'done' : 'open'}`)]);
    return { state: next, result: { batchId: batch.id, progress: batch.progress.videoShootEditingBrief } };
  },

  // `from` (optional): the status the person saw — a stale screen gets "This batch has already moved on." (DATA-04).
  // Done on the batch's current stage starts the next one, exactly like Mark Complete (LOGIC-02).
  'batch.setStageStatus'(state, p, ctx) {
    if (!STAGE_KEYS.includes(p.stage)) fail('Unknown production stage.');
    const norm = (v) => (v === 'Not Started' ? 'To Do' : v); // pre-v2 clients
    const status = norm(p.status);
    if (!STAGE_STATUSES.includes(status)) fail('Unknown status.');
    return withBatch(state, p.id, (batch) => {
      const shown = batch.stageStatus?.[p.stage];
      if (p.from != null && p.from !== '' && norm(p.from) !== (shown || 'To Do')) fail('This batch has already moved on.');
      if (shown === status) return batch;
      const stageStatus = { ...batch.stageStatus, [p.stage]: status };
      const next = STAGE_KEYS[STAGE_KEYS.indexOf(p.stage) + 1];
      if (status === 'Done' && batch.currentStage === p.stage && next && stageStatus[next] === 'To Do') stageStatus[next] = 'In Progress';
      return { ...batch, stageStatus };
    }, ctx);
  },

  // Starts stage 01. A no-op once the batch has started (a second click from a stale tab changes nothing).
  'batch.startProduction'(state, p, ctx) {
    return withBatch(state, p.id, (batch) => (
      batch.status !== 'To Do' ? batch : { ...batch, stageStatus: { ...batch.stageStatus, [STAGE_KEYS[0]]: 'In Progress' } }
    ), ctx);
  },

  // Marks the current stage Done and starts the next one. `stage` (the stage the user saw) is checked against the
  // current stage, so a stale tab cannot complete a stage nobody looked at.
  'batch.advance'(state, p, ctx) {
    const expected = p.stage == null || p.stage === '' ? '' : p.stage;
    if (expected && !STAGE_KEYS.includes(expected)) fail('Unknown production stage.');
    return withBatch(state, p.id, (batch) => {
      if (expected && (batch.status === 'Done' || batch.currentStage !== expected)) fail('This batch has already moved on.');
      if (batch.status === 'Done') return batch;
      const i = STAGE_KEYS.indexOf(batch.currentStage);
      const stageStatus = { ...batch.stageStatus, [batch.currentStage]: 'Done' };
      const next = STAGE_KEYS[i + 1];
      if (next && stageStatus[next] === 'To Do') stageStatus[next] = 'In Progress';
      return { ...batch, stageStatus };
    }, ctx);
  },

  'batch.delete'(state, p) {
    const batch = findOr404(state.batches, p.id, 'Batch');
    return {
      state: {
        ...state,
        batches: state.batches.filter((b) => b.id !== batch.id),
        events: state.events.filter((e) => e.batchId !== batch.id),
        activity: (state.activity || []).filter((a) => a.batchId !== batch.id),
      },
      result: { batchId: batch.id },
    };
  },

  'event.create'(state, p, ctx) {
    roomFor(state, 'events', 'events');
    const event = { id: ctx.newId(), ...cleanEventFields(pick(p, EVENT_FIELDS), state), createdAt: ctx.now, updatedAt: ctx.now };
    return { state: { ...state, events: [...state.events, event] }, result: { eventId: event.id } };
  },

  'event.update'(state, p, ctx) {
    const event = findOr404(state.events, p.id, 'Event');
    const patch = pick(checkNotesBase(patchOf(p), event.notes), EVENT_FIELDS);
    const merged = { ...event, ...patch };
    // Switching calendars without choosing a kind falls back to that calendar's default kind.
    if (Object.hasOwn(patch, 'calendarType') && !Object.hasOwn(patch, 'kind') && patch.calendarType !== event.calendarType) merged.kind = '';
    // Changing only the start between date-only and timed flips allDay accordingly.
    if (Object.hasOwn(patch, 'start') && !Object.hasOwn(patch, 'allDay')) delete merged.allDay;
    const checkSpan = ['start', 'end', 'allDay'].some((f) => Object.hasOwn(patch, f));
    const next = { ...ownedIf(patch, event), ...cleanEventFields(merged, state, { checkSpan }), updatedAt: ctx.now };
    return { state: { ...state, events: replaceById(state.events, next) }, result: { eventId: event.id } };
  },

  'event.delete'(state, p) {
    const event = findOr404(state.events, p.id, 'Event');
    return { state: { ...state, events: state.events.filter((e) => e.id !== event.id) }, result: { eventId: event.id } };
  },

  // loginId defaults to the first name's slug (made unique); isAdmin false, active true.
  'team.create'(state, p, ctx) {
    roomFor(state, 'team', 'team members');
    const name = text(p.name, 'Name', { required: true });
    const hasLogin = p.loginId != null && String(p.loginId).trim() !== '';
    const member = {
      id: ctx.newId(),
      name,
      role: text(p.role, 'Role', { max: MAX.role }),
      department: text(p.department, 'Department', { max: MAX.role }),
      loginId: hasLogin ? loginIdField(p.loginId, state.team, null)
        : suggestLoginId(name, new Set(state.team.map((t) => String(t.loginId || '').toLowerCase()))),
      isAdmin: p.isAdmin == null ? false : boolField(p.isAdmin, 'Admin'),
      active: p.active == null ? true : boolField(p.active, 'Active'),
    };
    return { state: { ...state, team: [...state.team, member] }, result: { teamMemberId: member.id } };
  },

  'team.update'(state, p) {
    const member = findOr404(state.team, p.id, 'Team member');
    const patch = pick(patchOf(p), ['name', 'role', 'department', 'loginId', 'isAdmin', 'active']);
    const merged = { ...member, ...patch };
    const next = {
      ...ownedIf(patch, member),
      name: text(merged.name, 'Name', { required: true }),
      role: text(merged.role, 'Role', { max: MAX.role }),
      department: text(merged.department, 'Department', { max: MAX.role }),
      loginId: Object.hasOwn(patch, 'loginId') ? loginIdField(patch.loginId, state.team, member.id) : member.loginId,
      isAdmin: Object.hasOwn(patch, 'isAdmin') ? boolField(patch.isAdmin, 'Admin') : member.isAdmin === true,
      active: Object.hasOwn(patch, 'active') ? boolField(patch.active, 'Active') : member.active !== false,
    };
    const team = replaceById(state.team, next);
    keepAnAdmin(state.team, team);
    return { state: { ...state, team }, result: { teamMemberId: member.id } };
  },

  'team.delete'(state, p, ctx) {
    const member = findOr404(state.team, p.id, 'Team member');
    const team = state.team.filter((t) => t.id !== member.id);
    keepAnAdmin(state.team, team);
    const clear = (list) => list.map((e) => (e.owner === member.id ? { ...e, owner: '', updatedAt: ctx.now } : e));
    // …and so do the client PIC slots they held.
    const clearPic = (c) => (c.pic && PIC_KEYS.some((k) => c.pic[k] === member.id)
      ? { ...c, pic: Object.fromEntries(PIC_KEYS.map((k) => [k, c.pic[k] === member.id ? '' : c.pic[k] || ''])), updatedAt: ctx.now } : c);
    return {
      state: {
        ...state,
        team,
        clients: clear(state.clients).map(clearPic),
        batches: clear(state.batches),
        events: clear(state.events),
      },
      result: { teamMemberId: member.id },
    };
  },

  // Removes the sample data and never anything the user added. A sample client that holds user data (a batch or an
  // event the user created) is kept with its months and loses its sample flag; its sample batches still go. A user
  // event that pointed at a removed sample batch keeps its client/month link. Sample team members still named as owner
  // by kept data (and sample admins) are kept and un-flagged. Pure, so a UI can preview the counts by
  // running it on the current state: result = { removed: { clients, months, batches, events, team, activity },
  // kept: { clients /* sample clients kept because they hold user data */ } }.
  'sample.clear'(state) {
    const unflag = (e) => {
      if (!e.sample) return e;
      const { sample, ...rest } = e;
      return rest;
    };
    const monthClient = new Map(state.months.map((m) => [m.id, m.clientId]));
    const userBatches = state.batches.filter((b) => !b.sample);
    const userEvents = state.events.filter((e) => !e.sample);
    const holders = new Set([
      ...userBatches.map((b) => b.clientId),
      ...userEvents.flatMap((e) => [e.clientId, monthClient.get(e.monthId)]),
      ...state.months.filter((m) => !m.sample).map((m) => m.clientId),
    ].filter(Boolean));
    const keptSample = state.clients.filter((c) => c.sample && holders.has(c.id)).length;
    const clients = state.clients.filter((c) => !c.sample || holders.has(c.id)).map(unflag);
    const clientIds = new Set(clients.map((c) => c.id));
    // A kept sample client loses the sample months' notes and Off flags (months the user edited are no longer sample).
    const months = state.months.filter((m) => !m.sample || clientIds.has(m.clientId))
      .map((m) => (m.sample ? { ...unflag(m), notes: '', off: false } : m));
    const monthIds = new Set(months.map((m) => m.id));
    const batches = userBatches;
    const batchIds = new Set(batches.map((b) => b.id));
    const events = userEvents.map((e) => {
      const link = {
        clientId: e.clientId && !clientIds.has(e.clientId) ? '' : e.clientId,
        monthId: e.monthId && !monthIds.has(e.monthId) ? '' : e.monthId,
        batchId: e.batchId && !batchIds.has(e.batchId) ? '' : e.batchId,
      };
      const changed = link.clientId !== e.clientId || link.monthId !== e.monthId || link.batchId !== e.batchId;
      return changed ? { ...e, ...link } : e;
    });
    // Sample members stay only while kept records name them as owner (or they are admins); activity goes when its
    // batch, client or month is gone, or its actor / assignee is a removed sample member (no dangling feed lines).
    const referenced = new Set([...clients, ...batches, ...events].map((e) => e.owner)
      .concat(clients.flatMap((c) => PIC_KEYS.map((k) => c.pic?.[k]))).filter(Boolean));
    // A fictional member kept only for attribution is switched off: nobody signs in as them (SEC-13).
    const team = state.team.filter((t) => !t.sample || t.isAdmin === true || referenced.has(t.id))
      .map((t) => (t.sample && t.isAdmin !== true ? { ...unflag(t), active: false } : unflag(t)));
    const switchedOff = team.filter((t) => t.active === false && state.team.some((o) => o.id === t.id && o.sample && o.active !== false)).length;
    const gone = new Set(state.team.filter((t) => !team.some((k) => k.id === t.id)).map((t) => t.id));
    const exists = (id, ids) => !id || ids.has(id);
    const activity = (state.activity || []).filter((a) => !a.sample && exists(a.batchId, batchIds) && exists(a.clientId, clientIds)
      && exists(a.monthId, monthIds) && !gone.has(a.actorId) && !(a.type === 'assigned' && gone.has(a.detail)));
    const removed = {
      clients: state.clients.length - clients.length,
      months: state.months.length - months.length,
      batches: state.batches.length - batches.length,
      events: state.events.length - events.length,
      team: state.team.length - team.length,
      activity: (state.activity || []).length - activity.length,
    };
    return {
      state: { ...state, team, clients, months, batches, events, activity },
      result: { removed, kept: { clients: keptSample, team: switchedOff } },
    };
  },
};

export function applyAction(state, action, ctx = {}) {
  const type = action && action.type;
  if (typeof type !== 'string' || !Object.hasOwn(handlers, type)) fail('Unknown action');
  const payload = action.payload && typeof action.payload === 'object' ? action.payload : {};
  const now = ctx.now || new Date().toISOString();
  const fullCtx = {
    now,
    today: ctx.today || isoDateOfTimestamp(now),
    newId: ctx.newId || createId,
    // Who is acting (recorded in the activity log): an existing team member id, otherwise ''.
    actorId: typeof ctx.actorId === 'string' && ctx.actorId && state.team.some((t) => t.id === ctx.actorId) ? ctx.actorId : '',
  };
  return handlers[type](state, payload, fullCtx);
}
